import { z } from "zod";

// Duplicate getCorsHeaders logic here to keep this file self-contained
// This is necessary because this file is shared with the client
function getCorsHeaders(req: Request): Record<string, string> {
  const origin = req.headers.get("Origin");
  const allowedOrigins = [
    "https://advisor-muse.lovable.app",
    "https://advisor.dev.speedynerdjack.com",
    "https://advisor.dev.sensiblymade.co",
    "https://bf98eef6-6c2c-421c-89be-70c301412a74.lovableproject.com",
    "https://id-52496f46--bf98eef6-6c2c-421c-89be-70c301412a74.lovable.app",
    "https://id-preview--bf98eef6-6c2c-421c-89be-70c301412a74.lovable.app",
    "https://id-preview-52496f46--bf98eef6-6c2c-421c-89be-70c301412a74.lovable.app",
    "https://preview--bf98eef6-6c2c-421c-89be-70c301412a74.lovable.app",
    "http://localhost:8080",
  ];

  const headers: Record<string, string> = {
    "Access-Control-Allow-Headers": "authorization, x-client-info, apikey, content-type, x-trigger-secret",
    "Access-Control-Allow-Methods": "POST, GET, OPTIONS, PUT, DELETE",
    "Access-Control-Allow-Credentials": "true",
  };

  if (origin && allowedOrigins.includes(origin)) {
    headers["Access-Control-Allow-Origin"] = origin;
  } else {
    headers["Access-Control-Allow-Origin"] = "*";
  }

  return headers;
}

/**
 * Validates a parsed request body against a Zod schema.
 * Returns a discriminated union:
 * - success: true, data: T
 * - success: false, response: Response (error response ready to return)
 */
export function validateParsedBody<T>(
  req: Request,
  body: any,
  schema: z.ZodSchema<T>
): { success: true; data: T } | { success: false; response: Response } {
  if (!body) {
    return {
      success: false,
      response: new Response(
        JSON.stringify({ error: "Request body required" }),
        {
          status: 400,
          headers: { ...getCorsHeaders(req), "Content-Type": "application/json" },
        }
      ),
    };
  }

  const result = schema.safeParse(body);

  if (!result.success) {
    console.error(`Validation failed for ${req.url}:`, JSON.stringify(result.error.format()));
    return {
      success: false,
      response: new Response(
        JSON.stringify({
          error: "Invalid input provided.",
          details: result.error.errors,
        }),
        {
          status: 400,
          headers: { ...getCorsHeaders(req), "Content-Type": "application/json" },
        }
      ),
    };
  }

  return { success: true, data: result.data };
}

/**
 * Common validation patterns
 */

// Common email typos to check
export const COMMON_EMAIL_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmil.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'hotmial.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
};

// Known disposable/temporary email domains to block
export const DISPOSABLE_EMAIL_DOMAINS = [
  'tempmail.com',
  'guerrillamail.com',
  '10minutemail.com',
  'mailinator.com',
  'throwaway.email',
  'temp-mail.org',
  'fakeinbox.com',
  'trashmail.com',
];

// Email validation with sanitization and typo detection
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "Email is required")
  .max(255, "Email must be less than 255 characters")
  .email("Please enter a valid email address")
  .refine((email) => {
    const domain = email.split('@')[1];
    return !DISPOSABLE_EMAIL_DOMAINS.includes(domain);
  }, "Disposable email addresses are not allowed");

// Helper function to check for common email typos
export const checkEmailTypos = (email: string): string | null => {
  const domain = email.split('@')[1];
  const suggestedDomain = COMMON_EMAIL_TYPOS[domain];
  
  if (suggestedDomain) {
    return `${email.split('@')[0]}@${suggestedDomain}`;
  }
  
  return null;
};

// Password validation
export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(128, "Password must be less than 128 characters")
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    "Password must contain at least one uppercase letter, one lowercase letter, and one number"
  );

// Text input with length limits (general purpose)
export const shortTextSchema = z
  .string()
  .trim()
  .min(1, "This field is required")
  .max(100, "Must be less than 100 characters");

export const mediumTextSchema = z
  .string()
  .trim()
  .min(1, "This field is required")
  .max(500, "Must be less than 500 characters");

export const longTextSchema = z
  .string()
  .trim()
  .min(1, "This field is required")
  .max(5000, "Must be less than 5000 characters");

// Optional text fields
export const optionalShortTextSchema = z
  .string()
  .trim()
  .max(100, "Must be less than 100 characters")
  .optional()
  .or(z.literal(""));

export const optionalMediumTextSchema = z
  .string()
  .trim()
  .max(500, "Must be less than 500 characters")
  .optional()
  .or(z.literal(""));

export const optionalLongTextSchema = z
  .string()
  .trim()
  .max(5000, "Must be less than 5000 characters")
  .optional()
  .or(z.literal(""));

/**
 * Board creation validation schemas
 */

// Brain dump form
export const brainDumpSchema = z.object({
  brainDump: z
    .string()
    .trim()
    .min(10, "Please provide more details (at least 10 characters)")
    .max(10000, "Brain dump must be less than 10,000 characters"),
});

// Custom board form
export const customBoardSchema = z.object({
  problem: z
    .string()
    .trim()
    .min(5, "Please describe the problem (at least 5 characters)")
    .max(500, "Problem description must be less than 500 characters"),
  context: optionalLongTextSchema,
});

// Persona validation
export const personaSchema = z.object({
  role_name: z
    .string()
    .trim()
    .min(2, "Role name must be at least 2 characters")
    .max(100, "Role name must be less than 100 characters"),
  mission: optionalLongTextSchema,
  principles: optionalLongTextSchema,
  competencies: optionalLongTextSchema,
  key_relationships: optionalLongTextSchema,
  typical_questions: optionalLongTextSchema,
});

// Board name validation
export const boardNameSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Board name is required")
    .max(100, "Board name must be less than 100 characters"),
});

/**
 * Chat and message validation schemas
 */

export const chatMessageSchema = z.object({
  message: z
    .string()
    .trim()
    .min(1, "Message cannot be empty")
    .max(10000, "Message must be less than 10,000 characters"),
});

/**
 * Project and workspace validation schemas
 */

export const projectNameSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Project name is required")
    .max(100, "Project name must be less than 100 characters"),
  description: optionalMediumTextSchema,
});

export const workspaceNameSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Workspace name is required")
    .max(100, "Workspace name must be less than 100 characters"),
});

/**
 * Tag validation
 */

export const tagSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Tag name is required")
    .max(50, "Tag name must be less than 50 characters")
    .regex(/^[a-zA-Z0-9\s-]+$/, "Tag can only contain letters, numbers, spaces, and hyphens"),
});

/**
 * Memory validation schemas
 */

export const memorySchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, "Memory content is required")
    .max(5000, "Memory content must be less than 5,000 characters"),
  category: z.enum([
    "personal_info",
    "professional_background",
    "goals_aspirations",
    "challenges_obstacles",
    "preferences_style",
    "relationships_network",
    "knowledge_expertise",
    "context_background",
    "other",
  ]),
});

/**
 * Admin validation schemas
 */

export const ipAddressSchema = z
  .string()
  .trim()
  .regex(
    /^(\d{1,3}\.){3}\d{1,3}$/,
    "Please enter a valid IPv4 address"
  )
  .refine((ip) => {
    const parts = ip.split('.').map(Number);
    return parts.every(part => part >= 0 && part <= 255);
  }, "Invalid IP address");

export const blockReasonSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(5, "Please provide a reason (at least 5 characters)")
    .max(500, "Reason must be less than 500 characters"),
});

/**
 * Sanitization utilities
 */

/**
 * Sanitize HTML to prevent XSS
 * Strips all HTML tags and dangerous characters
 */
export function sanitizeHTML(input: string): string {
  return input
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
}

/**
 * Sanitize for use in URLs
 */
export function sanitizeURL(input: string): string {
  return encodeURIComponent(input);
}

/**
 * Validate and sanitize text input
 * Returns sanitized string or throws validation error
 */
export function validateAndSanitize<T extends z.ZodType>(
  schema: T,
  input: unknown
): z.infer<T> {
  const result = schema.safeParse(input);
  
  if (!result.success) {
    throw new Error(result.error.issues[0].message);
  }
  
  return result.data;
}

/**
 * Helper to validate form data and return errors
 */
export function validateFormData<T extends z.ZodType>(
  schema: T,
  data: unknown
): { success: true; data: z.infer<T> } | { success: false; errors: Record<string, string> } {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  const errors: Record<string, string> = {};
  result.error.issues.forEach((err) => {
    const path = err.path.join(".");
    errors[path] = err.message;
  });
  
  return { success: false, errors };
}
